import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import FormulaExplainer from '../components/FormulaExplainer';
import ConvergencePanel from '../components/ConvergencePanel';
import BacktestPanel from '../components/BacktestPanel';
import BlindSpotsBanner from '../components/BlindSpotsBanner';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star,
  ArrowLeft,
  CircleNotch,
  DownloadSimple,
  CaretUp,
  CaretDown,
  ArrowsOutLineHorizontal,
  Target,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { syncConstraintsToDB } from '../components/MyPrediction';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import W3FamilyFinalVerdict from '../components/W3FamilyFinalVerdict';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DEFAULT_COLUMN_WIDTHS = {
  difference: 60,
  overall: 70,
  overall_pct: 75,
  month_total: 85,
  month_pct: 75,
  diff_pct: 70,
  month_yr1: 70,
  month_yr2: 70,
  month_yr3: 70,
  month_current_year: 75,
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(40, startWidthRef.current + diff);
    onResize(columnKey, newWidth);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  return (
    <th
      className={`relative ${className}`}
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      onClick={onClick}
    >
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">
        {children}
      </div>
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const BallTable = ({ ballData, columnWidths, onColumnResize, sortConfig, onSort, meta }) => {
  const matrix = ballData?.matrix || [];
  const mon = meta.current_month_abbr || 'Mon';
  const yr1 = meta.yr1;
  const yr2 = meta.yr2;
  const yr3 = meta.yr3;
  const cy = meta.current_year;

  // Total 2026 draws counted for this ball = sum of (prev_months_current_year + month_current_year)
  // across all rows. Since each draw contributes exactly one Diff row, this equals the number of
  // 2026 draws in the dataset for this ball.
  const total2026Draws = matrix.reduce(
    (s, r) => s + (r.prev_months_current_year || 0) + (r.month_current_year || 0),
    0
  );
  const cyShort = String(cy).slice(2);

  const sortedData = [...matrix].sort((a, b) => {
    const getVal = (r) => {
      const total2026 = (r.prev_months_current_year || 0) + (r.month_current_year || 0);
      if (sortConfig.key === 'total_2026') return total2026;
      if (sortConfig.key === 'total_2026_pct') {
        return total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
      }
      if (sortConfig.key === 'diff_pct_2026') {
        const pct = total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
        return pct - (r.overall_pct || 0);
      }
      return r[sortConfig.key] ?? 0;
    };
    const aVal = getVal(a);
    const bVal = getVal(b);
    return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
  });
  const topPredicted = new Set([...matrix].filter(r => !r.is_impossible).sort((a, b) => (b.probability_score || 0) - (a.probability_score || 0)).slice(0, 5).map(r => r.difference));

  const SortIcon = ({ sortKey }) => {
    if (sortConfig.key !== sortKey) return null;
    return sortConfig.direction === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const getDiffPctColor = (pct) => {
    if (pct > 0) return 'text-green-600';
    if (pct < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const headerCls = "px-2 py-2 text-xs uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '900px' }}>
        <thead className="bg-gray-100">
          <tr>
            <ResizableHeader width={columnWidths.difference} onResize={onColumnResize} columnKey="difference" onClick={() => onSort('difference')} className={headerCls}>
              Diff <SortIcon sortKey="difference" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.overall} onResize={onColumnResize} columnKey="overall" onClick={() => onSort('overall')} className={headerCls}>
              Overall <SortIcon sortKey="overall" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.overall_pct} onResize={onColumnResize} columnKey="overall_pct" onClick={() => onSort('overall_pct')} className={`${headerCls} bg-gray-200`}>
              Overall% <SortIcon sortKey="overall_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.total_2026 || 80} onResize={onColumnResize} columnKey="total_2026" onClick={() => onSort('total_2026')} className={`${headerCls} bg-teal-100`}>
              {cyShort} Tot <SortIcon sortKey="total_2026" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.total_2026_pct || 80} onResize={onColumnResize} columnKey="total_2026_pct" onClick={() => onSort('total_2026_pct')} className={`${headerCls} bg-teal-100`}>
              {cyShort}% <SortIcon sortKey="total_2026_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.diff_pct_2026 || 100} onResize={onColumnResize} columnKey="diff_pct_2026" onClick={() => onSort('diff_pct_2026')} className={`${headerCls} bg-pink-100`}>
              {cyShort} Diff% <SortIcon sortKey="diff_pct_2026" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_total} onResize={onColumnResize} columnKey="month_total" onClick={() => onSort('month_total')} className={`${headerCls} bg-blue-100`}>
              {mon} <SortIcon sortKey="month_total" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_pct} onResize={onColumnResize} columnKey="month_pct" onClick={() => onSort('month_pct')} className={`${headerCls} bg-blue-100`}>
              {mon}% <SortIcon sortKey="month_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.diff_pct} onResize={onColumnResize} columnKey="diff_pct" onClick={() => onSort('diff_pct')} className={`${headerCls} bg-purple-100`}>
              Diff% <SortIcon sortKey="diff_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr1} onResize={onColumnResize} columnKey="month_yr1" onClick={() => onSort('month_yr1')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr1).slice(2)} <SortIcon sortKey="month_yr1" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr2} onResize={onColumnResize} columnKey="month_yr2" onClick={() => onSort('month_yr2')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr2).slice(2)} <SortIcon sortKey="month_yr2" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr3} onResize={onColumnResize} columnKey="month_yr3" onClick={() => onSort('month_yr3')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr3).slice(2)} <SortIcon sortKey="month_yr3" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_current_year} onResize={onColumnResize} columnKey="month_current_year" onClick={() => onSort('month_current_year')} className={`${headerCls} bg-green-100`}>
              {mon}{String(cy).slice(2)} <SortIcon sortKey="month_current_year" />
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => (
            <tr key={row.difference} className={row.is_impossible ? 'bg-red-50 opacity-40' : topPredicted.has(row.difference) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={row.is_impossible ? {borderLeft: '4px solid #ef4444'} : topPredicted.has(row.difference) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}}>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono font-bold text-center">
                <span className={row.is_impossible ? 'line-through text-red-400' : ''}>{row.difference > 0 ? '+' : ''}{row.difference}</span>
                {row.next_value && <span className={`block text-[9px] font-normal ${row.is_impossible ? 'text-red-400' : 'text-gray-400'}`}>={row.next_value}</span>}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">
                {row.overall || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-gray-50">
                {row.overall_pct?.toFixed(1) || '-'}%
              </td>
              {(() => {
                const total2026 = (row.prev_months_current_year || 0) + (row.month_current_year || 0);
                const pct2026 = total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
                const diffVsOverall = pct2026 - (row.overall_pct || 0);
                const hasData = total2026 > 0 || (row.overall || 0) > 0;
                return (
                  <>
                    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-teal-50/50 text-teal-800">
                      {total2026 || '-'}
                    </td>
                    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-teal-50/40 text-teal-700">
                      {total2026 > 0 ? `${pct2026.toFixed(1)}%` : '-'}
                    </td>
                    <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-pink-50/40 ${getDiffPctColor(diffVsOverall)}`}>
                      {hasData ? `${diffVsOverall > 0 ? '+' : ''}${diffVsOverall.toFixed(1)}%` : '-'}
                    </td>
                  </>
                );
              })()}
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/50">
                {row.month_total || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/50">
                {row.month_pct?.toFixed(1) || '-'}%
              </td>
              <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-purple-50/50 ${getDiffPctColor(row.diff_pct)}`}>
                {row.diff_pct > 0 ? '+' : ''}{row.diff_pct?.toFixed(1) || '-'}%
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr1 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr2 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr3 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-green-50/50">
                {row.month_current_year || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Window3PerBall() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [baData, setBaData] = useState(null);
  const [w2Data, setW2Data] = useState(null);
  const [coverageData, setCoverageData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'overall', direction: 'desc' });
  const [w2SortConfig, setW2SortConfig] = useState({ key: 'overall_times', direction: 'desc' });
  const [mainTab, setMainTab] = useState('convergence');
  const [mySignPattern, setMySignPattern] = useState('');
  const [signApplied, setSignApplied] = useState(false);
  const [signSet, setSignSet] = useState(1);
  const [dbSets, setDbSets] = useState({});

  const [lastDrawNum, setLastDrawNum] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollRef = useRef(null);

  const handleSignSetChange = (s) => {
    setSignSet(s);
    const saved = dbSets[`C${s}`]?.sign_pattern || '';
    setMySignPattern(saved);
    setSignApplied(!!saved);
  };

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('window3_column_widths_v3');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_WIDTHS;
  });

  useEffect(() => {
    localStorage.setItem('window3_column_widths_v3', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
  };

  const resetColumnWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    toast.success('Column widths reset');
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsRefreshing(true);
    try {
      const [res, baRes, w2Res, covRes] = await Promise.all([
        axios.get(`${API}/analysis/per-ball-difference`),
        axios.get(`${API}/analysis/diff-before-after`),
        axios.get(`${API}/analysis/difference-matrix`),
        axios.get(`${API}/analysis/diff-range-coverage`),
      ]);
      setData(res.data);
      setBaData(baRes.data);
      setW2Data(w2Res.data);
      setCoverageData(covRes.data);
      setLastUpdated(new Date());
      // Track latest draw number from response
      const latestDraw = res.data?.last_draw_number || res.data?.total_draws;
      if (latestDraw) setLastDrawNum(latestDraw);
    } catch (error) {
      if (!silent) toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load constraint sets from DB
  useEffect(() => {
    (async () => {
      try {
        const csRes = await axios.get(`${API}/analysis/constraint-sets`);
        const cs = csRes.data || {};
        setDbSets(cs);
        const c1Sign = cs.C1?.sign_pattern || '';
        setMySignPattern(c1Sign);
        setSignApplied(!!c1Sign);
      } catch {}
    })();
  }, []);

  // Poll for new draws every 60 seconds
  useEffect(() => {
    const checkForNewDraw = async () => {
      try {
        const res = await axios.get(`${API}/draws/stats`);
        const latestDraw = res.data?.latest_draw;
        if (latestDraw && lastDrawNum && latestDraw > lastDrawNum) {
          toast.info('New draw detected! Refreshing analysis...', { duration: 3000 });
          fetchData(true);
        }
      } catch {}
    };
    pollRef.current = setInterval(checkForNewDraw, 60000);
    return () => clearInterval(pollRef.current);
  }, [lastDrawNum, fetchData]);

  // Refresh on page focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && lastDrawNum) {
        axios.get(`${API}/draws/stats`).then(res => {
          const latestDraw = res.data?.latest_draw;
          if (latestDraw && latestDraw > lastDrawNum) {
            toast.info('New draw detected! Refreshing analysis...', { duration: 3000 });
            fetchData(true);
          }
        }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [lastDrawNum, fetchData]);

  // Instant refresh when a draw is added/updated/deleted elsewhere in the app
  useEffect(() => {
    const handleDrawsUpdated = () => {
      toast.info('Draw data changed — refreshing Window 3...', { duration: 2500 });
      fetchData(true);
    };
    window.addEventListener('euromillions:draws-updated', handleDrawsUpdated);
    return () => window.removeEventListener('euromillions:draws-updated', handleDrawsUpdated);
  }, [fetchData]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/analysis/per-ball-difference/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `per_ball_difference_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  const mon = data?.current_month_abbr || 'Mon';
  const monFull = data?.current_month_name || 'Month';
  const cy = data?.current_year || '';
  const yr1 = data?.yr1 || '';
  const yr3 = data?.yr3 || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />

      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft size={18} />
              </Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
                <Star size={24} weight="fill" className="text-amber-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 3 (A-X)</h1>
                <p className="text-xs text-gray-500">Per-Ball Difference Analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {lastUpdated && (
                <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="auto-update-indicator">
                  {isRefreshing && <CircleNotch size={14} className="animate-spin text-blue-500" />}
                  <span>Draw #{data?.last_draw_number || '—'}</span>
                  <span className="text-gray-300">|</span>
                  <span>{lastUpdated.toLocaleTimeString()}</span>
                  <button
                    onClick={() => fetchData(true)}
                    disabled={isRefreshing}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    title="Refresh now"
                    data-testid="refresh-btn"
                  >
                    <ArrowsClockwise size={14} className={isRefreshing ? 'animate-spin text-blue-500' : 'text-gray-400 hover:text-gray-600'} />
                  </button>
                </div>
              )}
              <Button onClick={resetColumnWidths} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700" title="Reset column widths">
                <ArrowsOutLineHorizontal size={18} />
              </Button>
              <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
                {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <W3FamilyFinalVerdict activeWindow="W3" />
        <WindowNarrative windowId="W3" {...NARRATIVES.W3} className="mb-5" />
        <PageConsensusBanner className="mb-4" />
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Analysis Type</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>Per Ball</p>
                <p className="text-xs text-gray-400">Ball 1 to Ball 5</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Month</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  {monFull} {cy}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Latest Draw</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>#{data.last_draw_number || '—'}</p>
                <p className="text-xs text-gray-400">{data.last_draw_date || ''}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Auto-Refresh</p>
                <p className="text-xs text-gray-600 mt-1">
                  Checks every 60s for new draws. Also refreshes on page focus.
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Last: {lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}
                </p>
              </div>
            </div>

            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
              <ArrowsOutLineHorizontal size={14} />
              <span>Drag column borders to resize | <strong>Diff%</strong> = {mon}% - Overall% | Auto-adapts to current month</span>
            </div>


            {/* Formula Explainer (collapsible) */}
            <FormulaExplainer context="ball-diff" />

            {/* Main Tab Switcher — W3: Complementary F1/F2/Convergence + diff tabs */}
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5 mb-4 flex-wrap" data-testid="w3-main-tabs">
              <button onClick={() => setMainTab('convergence')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'convergence' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-convergence">
                🎯 Convergence
              </button>
              <button onClick={() => setMainTab('formula1')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'formula1' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-formula1">
                Formula 1 (Long-term)
              </button>
              <button onClick={() => setMainTab('formula2')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'formula2' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-formula2">
                Formula 2 (Next-draw)
              </button>
              <button onClick={() => setMainTab('prediction')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'prediction' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-prediction">
                Final Diff Prediction <span className="text-[10px] font-normal opacity-70">(2 blocks)</span>
              </button>
              <button onClick={() => setMainTab('beforeafter')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'beforeafter' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-beforeafter">
                Diff Before & After <span className="text-[10px] font-normal opacity-70">(6 blocks)</span>
              </button>
              <button onClick={() => setMainTab('balls')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'balls' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-balls">
                Ball Difference Analysis <span className="text-[10px] font-normal opacity-70">(5 blocks)</span>
              </button>
              <button onClick={() => setMainTab('coverage')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'coverage' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-coverage">
                Diff Range Coverage <span className="text-[10px] font-normal opacity-70">(9 blocks)</span>
              </button>
              <button onClick={() => setMainTab('monthly')} className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'monthly' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-monthly">
                Sum of Difference <span className="text-[10px] font-normal opacity-70">(2 blocks)</span>
              </button>
            </div>

            {/* === CONVERGENCE TAB === */}
            {mainTab === 'convergence' && data?.balls && (
              <W3BallDiffConvergenceTab data={data} />
            )}

            {/* === FORMULA 1 TAB === */}
            {mainTab === 'formula1' && data?.balls && (
              <W3BallDiffFormulaTab data={data} which="f1" />
            )}

            {/* === FORMULA 2 TAB === */}
            {mainTab === 'formula2' && data?.balls && (
              <W3BallDiffFormulaTab data={data} which="f2" />
            )}

            {/* Tab 0: Final Diff Prediction (diff-only) */}
            {mainTab === 'prediction' && data?.balls && (() => {
              // Step 1: Build candidates per ball — UNFILTERED by direction, ranked by score
              const ballCandidates = ['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, bi) => {
                const matrix = data.balls?.[bk]?.matrix || [];
                const allValid = [...matrix].filter(r =>
                  r.probability_score && r.probability_score > 0 && !r.is_impossible
                ).sort((a, b) => b.probability_score - a.probability_score);
                return { key: bk, label: `Ball ${bi + 1}`, last: data.last_values?.[bk], candidates: allValid };
              });
              // Step 2: Sequential pick enforcing Ball1 < Ball2 < Ball3 < Ball4 < Ball5
              let floorValue = 0;
              const ballPicks = ballCandidates.map((bc) => {
                const valid = bc.candidates.filter(r => r.next_value > floorValue);
                const top5 = valid.slice(0, 5);
                const top5Diffs = new Set(top5.map(r => r.difference));
                const overdueDue = valid
                  .filter(r => r.is_overdue && !top5Diffs.has(r.difference))
                  .sort((a, b) => (b.overdue_ratio || 0) - (a.overdue_ratio || 0))
                  .slice(0, 3);
                if (top5.length > 0) floorValue = top5[0].next_value;
                return { ...bc, top5, overdueDue };
              });

              return (
                <div className="space-y-4">
                  {/* Combined Prediction Header */}
                  <div className="bg-gradient-to-r from-[#002FA7] to-indigo-800 rounded-xl p-6 text-white" data-testid="final-prediction-header">
                    <h2 className="text-2xl font-bold mb-1">Final Diff Prediction — Window 3</h2>
                    <p className="text-sm text-blue-200">
                      Per-Ball Most Probable Differences (12-weight scoring) + what historically comes after each ball's current diff.
                    </p>
                  </div>

                  {/* Per-Ball Most Deserving Differences */}
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3">
                      <h3 className="font-bold">Block 1 — Most Deserving Difference Per Ball</h3>
                      <p className="text-xs text-emerald-200 mt-0.5">
                        Top 5 by 12-weight probability score — direction-agnostic. Backtested +45.5% top-1 accuracy over 50 draws.
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {ballPicks.map((bp, bi) => (
                          <div key={bp.key} className="border rounded-lg overflow-hidden">
                            <div className="bg-slate-700 text-white px-3 py-2 flex items-center justify-between">
                              <span className="font-bold">{bp.label}</span>
                              <span className="text-[10px] text-white/60">Last: {bp.last}</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {bp.top5.map((row, ri) => {
                                const bd = row.score_breakdown || {};
                                const sign = row.difference > 0 ? '+' : row.difference < 0 ? '-' : '=';
                                return (
                                  <div key={row.difference} className={`p-3 ${ri === 0 ? 'bg-emerald-50' : ''}`}>
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className={`text-xs font-bold ${sign === '+' ? 'text-emerald-500' : sign === '-' ? 'text-red-400' : 'text-gray-400'}`}>
                                          {sign === '+' ? '\u25B2' : sign === '-' ? '\u25BC' : '='}
                                        </span>
                                        <span className={`font-mono font-bold text-xl ${row.difference > 0 ? 'text-emerald-600' : row.difference < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                                          {row.difference > 0 ? '+' : ''}{row.difference}
                                        </span>
                                      </div>
                                      <span className="font-mono font-bold text-lg text-gray-800">{row.next_value}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-gray-500">Score: <strong>{row.probability_score}</strong></span>
                                      {row.is_overdue && (
                                        <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full">
                                          {row.overdue_ratio}x DUE
                                        </span>
                                      )}
                                    </div>
                                    {/* Weight breakdown for all candidates */}
                                    <div className="mt-1.5 space-y-0.5">
                                      {[
                                        { k: 'w6_lifetime_month_absence', l: 'W6 LifeAbsence', c: 'text-gray-500' },
                                        { k: 'w7_mirror_reversion', l: 'W7 Mirror', c: 'text-gray-500' },
                                        { k: 'w9_overdue_boost', l: 'W9 Overdue', c: 'text-gray-500' },
                                        { k: 'w10_target_recency', l: 'W10 Recency', c: 'text-blue-600' },
                                        { k: 'w11_direction_alt', l: 'W11 DirAlt', c: 'text-purple-600' },
                                        { k: 'w12_position_range', l: 'W12 PosRange', c: 'text-amber-600' },
                                        { k: 'w13_markov_transition', l: 'W13 Markov', c: 'text-cyan-600' },
                                      ].map(w => {
                                        const val = bd[w.k] || 0;
                                        if (val === 0 && ri > 0) return null;
                                        return (
                                          <div key={w.k} className="flex items-center justify-between text-[9px]">
                                            <span className={w.c}>{w.l}</span>
                                            <span className={`font-mono font-bold ${val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-500' : 'text-gray-300'}`}>
                                              {val > 0 ? '+' : ''}{val}
                                            </span>
                                          </div>
                                        );
                                      })}
                                      {ri === 0 && (
                                        <>
                                          <div className="border-t border-gray-200 mt-1 pt-1 flex items-center justify-between text-[8px] text-gray-400">
                                            <span>gap={bd.target_gap} L15={bd.target_l15}</span>
                                            <span>dirs: {bd.last_2_dirs || '—'}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-[8px] text-gray-400">
                                            <span>W1:{bd.w1_month_deficit} W2:{bd.w2_absence}</span>
                                            <span>W5:{bd.w5_yearly_penalty} W8:{bd.w8_month_affinity}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-[8px] text-gray-400">
                                            <span className="text-cyan-600">Markov: {bd.w13_markov_pct}% ({bd.w13_markov_count}x)</span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Additional overdue diffs not in top 5 */}
                              {bp.overdueDue.length > 0 && (
                                <>
                                  <div className="px-3 py-1.5 bg-red-50 border-t-2 border-red-200">
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">Also Overdue</span>
                                  </div>
                                  {bp.overdueDue.map((row) => {
                                    const bd2 = row.score_breakdown || {};
                                    return (
                                    <div key={row.difference} className="p-3 bg-red-50/50">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-1.5">
                                          <span className={`text-xs font-bold ${row.difference > 0 ? 'text-emerald-500' : row.difference < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                                            {row.difference > 0 ? '\u25B2' : row.difference < 0 ? '\u25BC' : '='}
                                          </span>
                                          <span className={`font-mono font-bold text-lg ${row.difference > 0 ? 'text-emerald-600' : row.difference < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                                            {row.difference > 0 ? '+' : ''}{row.difference}
                                          </span>
                                        </div>
                                        <span className="font-mono font-bold text-gray-700">{row.next_value}</span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-gray-500">Score: {row.probability_score}</span>
                                        <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full">
                                          {row.overdue_ratio}x DUE
                                        </span>
                                      </div>
                                      <div className="mt-1 text-[9px] flex flex-wrap gap-x-2 text-gray-400">
                                        <span className="text-blue-600">W10:{bd2.w10_target_recency}</span>
                                        <span className="text-purple-600">W11:{bd2.w11_direction_alt}</span>
                                        <span className="text-amber-600">W12:{bd2.w12_position_range}</span>
                                        <span className="text-cyan-600">W13:{bd2.w13_markov_transition}</span>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* After Transition Analysis per Ball */}
                  {baData?.balls && (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 py-3">
                        <h3 className="font-bold">Block 2 — What Comes After Current Diff (Per Ball)</h3>
                        <p className="text-xs text-amber-200 mt-0.5">Top historical transitions after each ball's current difference + diffs that NEVER followed it (missing).</p>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          {['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, bi) => {
                            const bd = baData.balls?.[bk];
                            if (!bd) return null;
                            const topAfter = (bd.after || []).slice(0, 4);
                            // Compute missing: diffs in the full matrix that never appeared after current diff
                            const afterDiffSet = new Set((bd.after || []).map(a => a.diff));
                            const matrixRows = data.balls?.[bk]?.matrix || [];
                            const missing = matrixRows
                              .filter(r => !r.is_impossible && r.overall > 0 && !afterDiffSet.has(r.difference))
                              .sort((a, b) => b.overall - a.overall)
                              .slice(0, 8)
                              .map(r => ({
                                diff: r.difference,
                                label: `${r.difference > 0 ? '+' : ''}${r.difference}`,
                                target: r.next_value,
                                overall_count: r.overall,
                                overall_pct: r.overall_pct?.toFixed(1) || '0',
                              }));
                            return (
                              <div key={bk} className="border rounded-lg overflow-hidden">
                                <div className="bg-amber-700 text-white px-3 py-2 flex items-center justify-between">
                                  <span className="font-bold">Ball {bi + 1}</span>
                                  <span className="text-[10px] text-white/70">After {bd.last_diff_label}</span>
                                </div>
                                {/* Top After */}
                                <div className="divide-y divide-gray-100">
                                  {topAfter.map((a, ai) => (
                                    <div key={a.diff} className={`px-3 py-2 flex items-center justify-between ${ai === 0 ? 'bg-amber-50' : ''}`}>
                                      <div className="flex items-center gap-2">
                                        <span className={`font-mono font-bold ${a.diff > 0 ? 'text-emerald-600' : a.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{a.label}</span>
                                        <span className="inline-flex w-6 h-6 rounded-full bg-amber-600 text-white items-center justify-center font-mono font-bold text-[9px]">{a.target}</span>
                                      </div>
                                      <span className="text-[10px] text-gray-500 font-mono">{a.count}x ({a.pct}%)</span>
                                    </div>
                                  ))}
                                </div>
                                {/* Missing After */}
                                {missing.length > 0 && (
                                  <>
                                    <div className="px-3 py-1.5 bg-red-50 border-t-2 border-red-200">
                                      <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">Never after {bd.last_diff_label}</span>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                      {missing.map((m) => (
                                        <div key={m.diff} className="px-3 py-1.5 flex items-center justify-between bg-red-50/40">
                                          <div className="flex items-center gap-2">
                                            <span className={`font-mono font-bold text-sm ${m.diff > 0 ? 'text-emerald-600' : m.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{m.label}</span>
                                            <span className="inline-flex w-6 h-6 rounded-full bg-gray-400 text-white items-center justify-center font-mono font-bold text-[9px]">{m.target}</span>
                                          </div>
                                          <span className="text-[9px] text-gray-400 font-mono">overall: {m.overall_count}x ({m.overall_pct}%)</span>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })()}

            {/* Tab: Diff Before & After */}
            {mainTab === 'beforeafter' && baData?.balls && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-xl p-5 text-white" data-testid="ba-header">
                  <h2 className="text-xl font-bold mb-1">Block 1 — Difference Before & After Analysis</h2>
                  <p className="text-sm text-amber-100">When a specific difference occurs for a ball, what differences historically came before and after it? Shows the transition patterns around the current (last) difference.</p>
                </div>
                {['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, bi) => {
                  const bd = baData.balls[bk];
                  if (!bd) return null;
                  return (
                    <div key={bk} className="bg-white border rounded-lg overflow-hidden" data-testid={`ba-${bk}`}>
                      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 py-3 flex items-center justify-between">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-bold text-lg">Block {bi + 2} — Ball {bi + 1}</span>
                          <span className="text-sm text-white/70">Last Value: <span className="font-mono font-bold text-white">{bd.last_value}</span></span>
                          <span className="text-sm text-white/70">Current Diff: <span className={`font-mono font-bold ${bd.last_diff > 0 ? 'text-emerald-400' : bd.last_diff < 0 ? 'text-red-400' : 'text-gray-400'}`}>{bd.last_diff_label}</span></span>
                          {bd.prev_diff_label && (
                            <span className="text-sm text-white/70">Prev Diff: <span className={`font-mono font-bold ${bd.prev_diff > 0 ? 'text-emerald-400' : bd.prev_diff < 0 ? 'text-red-400' : 'text-gray-400'}`}>{bd.prev_diff_label}</span></span>
                          )}
                        </div>
                        <span className="bg-white/20 px-3 py-1 rounded-full text-xs">Occurred {bd.occurrences}x historically</span>
                      </div>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Before */}
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                            What came BEFORE {bd.last_diff_label} ({bd.total_before} transitions)
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1.5 text-left text-[10px] uppercase">Diff</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">Count</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">%</th>
                                  <th className="px-2 py-1.5 text-[10px] uppercase">Bar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(bd.before || []).slice(0, 10).map((b, idx) => (
                                  <tr key={b.diff} className={idx === 0 ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx === 0 ? { borderLeft: '3px solid #d97706' } : {}}>
                                    <td className="px-2 py-1.5 border-b">
                                      <span className={`font-mono font-bold ${b.diff > 0 ? 'text-emerald-600' : b.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{b.label}</span>
                                    </td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center">{b.count}</td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{b.pct}%</td>
                                    <td className="px-2 py-1.5 border-b">
                                      <div className="bg-gray-100 rounded h-3 overflow-hidden">
                                        <div className="h-full bg-amber-500 rounded" style={{ width: `${(b.count / (bd.before[0]?.count || 1)) * 100}%` }} />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {/* After */}
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                            What comes AFTER {bd.last_diff_label} ({bd.total_after} transitions)
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1.5 text-left text-[10px] uppercase">Diff</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">Target</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">Count</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">Till 2025</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">2026</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">Diff</th>
                                  <th className="px-2 py-1.5 text-center text-[10px] uppercase">%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(bd.after || []).slice(0, 10).map((a, idx) => (
                                  <tr key={a.diff} className={idx === 0 ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx === 0 ? { borderLeft: '3px solid #2563eb' } : {}}>
                                    <td className="px-2 py-1.5 border-b">
                                      <span className={`font-mono font-bold ${a.diff > 0 ? 'text-emerald-600' : a.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{a.label}</span>
                                    </td>
                                    <td className="px-2 py-1.5 border-b text-center">
                                      {a.valid !== false ? (
                                        <span className="inline-flex w-7 h-7 rounded-full bg-blue-600 text-white items-center justify-center font-mono font-bold text-[10px]">{a.target}</span>
                                      ) : (
                                        <span className="text-gray-300 text-[10px]">out</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center">{a.count}</td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center text-gray-600">{a.till_2025_pct}%</td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold text-violet-700">{a.y2026_pct}%</td>
                                    <td className="px-2 py-1.5 border-b text-center">
                                      <span className={`font-mono font-bold text-[10px] px-1.5 py-0.5 rounded ${a.year_diff > 0 ? 'text-emerald-700 bg-emerald-50' : a.year_diff < 0 ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>
                                        {a.year_diff > 0 ? '+' : ''}{a.year_diff}%
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{a.pct}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {/* Pair After (2nd-order Markov) */}
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                            After pair {bd.pair_label || '?'} ({bd.total_pair_after || 0} transitions)
                          </h4>
                          {bd.pair_after && bd.pair_after.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-2 py-1.5 text-left text-[10px] uppercase">Diff</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">Target</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">Count</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">Till 2025</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">2026</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">Diff</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] uppercase">%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bd.pair_after.slice(0, 10).map((pa, idx) => (
                                    <tr key={pa.diff} className={idx === 0 ? 'bg-violet-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx === 0 ? { borderLeft: '3px solid #7c3aed' } : {}}>
                                      <td className="px-2 py-1.5 border-b">
                                        <span className={`font-mono font-bold ${pa.diff > 0 ? 'text-emerald-600' : pa.diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{pa.label}</span>
                                      </td>
                                      <td className="px-2 py-1.5 border-b text-center">
                                        {pa.valid !== false ? (
                                          <span className="inline-flex w-7 h-7 rounded-full bg-violet-600 text-white items-center justify-center font-mono font-bold text-[10px]">{pa.target}</span>
                                        ) : (
                                          <span className="text-gray-300 text-[10px]">out</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1.5 border-b font-mono text-center">{pa.count}</td>
                                      <td className="px-2 py-1.5 border-b font-mono text-center text-gray-600">{pa.till_2025_pct}%</td>
                                      <td className="px-2 py-1.5 border-b font-mono text-center font-bold text-violet-700">{pa.y2026_pct}%</td>
                                      <td className="px-2 py-1.5 border-b text-center">
                                        <span className={`font-mono font-bold text-[10px] px-1.5 py-0.5 rounded ${pa.year_diff > 0 ? 'text-emerald-700 bg-emerald-50' : pa.year_diff < 0 ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>
                                          {pa.year_diff > 0 ? '+' : ''}{pa.year_diff}%
                                        </span>
                                      </td>
                                      <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{pa.pct}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center text-gray-400 py-6 text-sm">
                              Pair {bd.pair_label || '?'} not found — no data
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}


            {/* Tab 2: Ball Difference Analysis */}
            {mainTab === 'balls' && (
            <div className="space-y-4">

            <Tabs defaultValue="ball_1" className="space-y-4">
              <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
                {Object.entries(ballLabels).map(([key, label]) => (
                  <TabsTrigger key={key} value={key} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">
                    {label} {data?.last_values?.[key] ? <span className="text-[10px] ml-1 opacity-70">(Last: {data.last_values[key]})</span> : ''}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(ballLabels).map(([key, label]) => {
                const matrix = data.balls?.[key]?.matrix || [];

                return (
                  <TabsContent key={key} value={key}>
                    {/* Most Probable Numbers — on top */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-5 mb-4 text-white">
                      <div className="flex items-center gap-2 mb-3">
                        <Target size={24} weight="bold" />
                        <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                          Block 1 — Most Probable Differences for {label}
                        </h3>
                      </div>
                      <p className="text-sm text-white/80 mb-3">
                        13-Weight scoring: W1-W9(70%) + W10 Recency(10%) + W11 DirAlt(8%) + W12 PosRange(10%) + W13 Markov(2%)
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {matrix
                          .filter(r => r.probability_score > 0)
                          .sort((a, b) => b.probability_score - a.probability_score)
                          .slice(0, 5)
                          .map((row, idx) => {
                            const bd = row.score_breakdown || {};
                            return (
                              <div key={row.difference} className="bg-white/20 backdrop-blur rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold font-mono">
                                  {row.difference > 0 ? '+' : ''}{row.difference}
                                </div>
                                <div className="text-xs text-white/60 font-mono">Number {row.next_value}</div>
                                <div className="text-sm text-white/90 font-bold mt-1">Score: {row.probability_score}</div>
                                {row.is_overdue && (
                                  <div className="text-[10px] bg-red-400/30 text-red-100 rounded-full px-2 py-0.5 mt-1 font-bold">
                                    OVERDUE {row.overdue_ratio}x (gap {row.overdue_gap}/{row.overdue_avg_gap})
                                  </div>
                                )}
                                <div className="text-xs text-white/70 mt-2 text-left space-y-0.5">
                                  <div className="flex justify-between"><span>W1 Mon Deficit:</span><span>{bd.w1_month_deficit}</span></div>
                                  <div className="flex justify-between"><span>W2 Absence:</span><span>{bd.w2_absence}</span></div>
                                  <div className="flex justify-between"><span>W3 Absent Yrs:</span><span>{bd.w3_absent_years}</span></div>
                                  <div className="flex justify-between"><span>W4 Peer Lag:</span><span>{bd.w4_peer_lag}</span></div>
                                  <div className="flex justify-between"><span>W5 Yr Signal:</span><span>{bd.w5_yearly_penalty}</span></div>
                                  <div className="flex justify-between"><span>W6 Life Absence:</span><span>{bd.w6_lifetime_month_absence}</span></div>
                                  <div className="flex justify-between"><span>W7 Mirror Rev:</span><span>{bd.w7_mirror_reversion}</span></div>
                                  <div className="flex justify-between"><span>W8 Mo Affinity:</span><span>{bd.w8_month_affinity}</span></div>
                                  <div className={`flex justify-between ${bd.w9_overdue_boost > 0 ? 'font-bold text-red-200' : ''}`}><span>W9 Overdue:</span><span>{bd.w9_overdue_boost}</span></div>
                                  <div className={`flex justify-between ${bd.w10_target_recency > 0 ? 'text-blue-200' : ''}`}><span>W10 Recency:</span><span>{bd.w10_target_recency}</span></div>
                                  <div className={`flex justify-between ${bd.w11_direction_alt > 0 ? 'text-purple-200' : ''}`}><span>W11 DirAlt:</span><span>{bd.w11_direction_alt}</span></div>
                                  <div className={`flex justify-between ${bd.w12_position_range > 0 ? 'text-amber-200' : ''}`}><span>W12 PosRange:</span><span>{bd.w12_position_range}</span></div>
                                  <div className={`flex justify-between ${bd.w13_markov_transition > 0 ? 'text-cyan-200 font-bold' : bd.w13_markov_transition < 0 ? 'text-red-200' : ''}`}><span>W13 Markov:</span><span>{bd.w13_markov_transition} ({bd.w13_markov_pct}%)</span></div>
                                </div>
                                {idx === 0 && (
                                  <div className="text-xs bg-sky-100 text-sky-900 rounded-full px-2 py-0.5 mt-2 font-bold">TOP PICK</div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
                          Block 2 — {label} Difference Analysis
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Difference between consecutive draws | <span className="text-green-600 font-bold">+Diff%</span> = More in {monFull} than Overall | <span className="text-red-600 font-bold">-Diff%</span> = Less in {monFull}
                        </p>
                      </div>
                      <BallTable
                        ballData={data.balls[key]}
                        columnWidths={columnWidths}
                        onColumnResize={handleColumnResize}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        meta={data}
                      />
                    </div>

                    {/* Mirror Pair Comparison Table */}
                    {(() => {
                      const mirrorPairs = data.balls?.[key]?.mirror_pairs || [];
                      if (mirrorPairs.length === 0) return null;
                      return (
                        <div className="bg-gradient-to-r from-indigo-600 to-violet-700 rounded-lg p-5 mt-4 text-white" data-testid={`mirror-pair-table-${key}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                              Block 3 — Mirror Pair Comparison — {label}
                            </h3>
                          </div>
                          <p className="text-sm text-white/80 mb-4">
                            +N vs -N side by side. When the {monFull} gap is wider than Overall gap, reversion pressure is amplified.
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-white/70 border-b border-white/20">
                                  <th className="px-2 py-2 text-center">|Diff|</th>
                                  <th className="px-2 py-2 text-center bg-green-500/20">+N Ovr%</th>
                                  <th className="px-2 py-2 text-center bg-red-500/20">-N Ovr%</th>
                                  <th className="px-2 py-2 text-center">Ovr Gap</th>
                                  <th className="px-2 py-2 text-center bg-green-500/20">+N {data?.current_month_abbr || 'Mo'}%</th>
                                  <th className="px-2 py-2 text-center bg-red-500/20">-N {data?.current_month_abbr || 'Mo'}%</th>
                                  <th className="px-2 py-2 text-center">{data?.current_month_abbr || 'Mo'} Gap</th>
                                  <th className="px-2 py-2 text-center">Amp%</th>
                                  <th className="px-2 py-2 text-center bg-green-500/20">+N Score</th>
                                  <th className="px-2 py-2 text-center bg-red-500/20">-N Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mirrorPairs.map(p => {
                                  const monthGapAbs = Math.abs(p.month_gap);
                                  const ovrGapAbs = Math.abs(p.overall_gap);
                                  const amplified = monthGapAbs > ovrGapAbs && p.month_gap !== 0;
                                  const monthGapWider = Math.abs(p.month_gap) - Math.abs(p.overall_gap);
                                  return (
                                    <tr key={p.abs_diff} className={`border-b border-white/10 ${amplified ? 'bg-yellow-500/10' : ''}`}>
                                      <td className="px-2 py-1.5 text-center font-bold font-mono">{p.abs_diff}</td>
                                      <td className={`px-2 py-1.5 text-center font-mono ${p.pos_is_impossible ? 'text-white/30 line-through' : ''}`}>{p.pos_overall_pct}%</td>
                                      <td className={`px-2 py-1.5 text-center font-mono ${p.neg_is_impossible ? 'text-white/30 line-through' : ''}`}>{p.neg_overall_pct}%</td>
                                      <td className={`px-2 py-1.5 text-center font-mono font-bold ${p.overall_gap > 0 ? 'text-red-300' : p.overall_gap < 0 ? 'text-green-300' : 'text-white/50'}`}>
                                        {p.overall_gap > 0 ? '+' : ''}{p.overall_gap}%
                                      </td>
                                      <td className={`px-2 py-1.5 text-center font-mono ${p.pos_month_pct > p.pos_overall_pct ? 'text-yellow-300 font-bold' : ''} ${p.pos_is_impossible ? 'text-white/30 line-through' : ''}`}>{p.pos_month_pct}%</td>
                                      <td className={`px-2 py-1.5 text-center font-mono ${p.neg_month_pct > p.neg_overall_pct ? 'text-yellow-300 font-bold' : ''} ${p.neg_is_impossible ? 'text-white/30 line-through' : ''}`}>{p.neg_month_pct}%</td>
                                      <td className={`px-2 py-1.5 text-center font-mono font-bold ${p.month_gap > 0 ? 'text-red-300' : p.month_gap < 0 ? 'text-green-300' : 'text-white/50'}`}>
                                        {p.month_gap > 0 ? '+' : ''}{p.month_gap}%
                                      </td>
                                      <td className={`px-2 py-1.5 text-center font-mono font-bold ${amplified ? 'text-yellow-300' : 'text-white/40'}`}>
                                        {amplified ? `${monthGapWider > 0 ? '+' : ''}${monthGapWider.toFixed(1)}%` : '—'}
                                      </td>
                                      <td className={`px-2 py-1.5 text-center font-mono font-bold ${p.pos_is_impossible ? 'text-white/30' : 'text-green-300'}`}>{p.pos_is_impossible ? '—' : p.pos_score}</td>
                                      <td className={`px-2 py-1.5 text-center font-mono font-bold ${p.neg_is_impossible ? 'text-white/30' : 'text-red-300'}`}>{p.neg_is_impossible ? '—' : p.neg_score}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-3 text-xs text-white/60 flex gap-4 flex-wrap">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span> Highlighted = {monFull} gap wider than Overall (amplified reversion)</span>
                            <span className="flex items-center gap-1"><span className="text-green-300 font-bold">Green</span> = +N side dominates</span>
                            <span className="flex items-center gap-1"><span className="text-red-300 font-bold">Red</span> = -N side dominates</span>
                            <span className="flex items-center gap-1"><span className="text-yellow-300 font-bold">Yellow %</span> = Month% &gt; Overall% (month affinity)</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Rare in Month — Diffs with 0/very low appearance in this month across 22 years */}
                    {(() => {
                      const feasible = matrix.filter(r => !r.is_impossible && r.overall >= 3);
                      const neverInMonth = feasible.filter(r => r.month_total === 0).sort((a, b) => b.overall - a.overall);
                      const onceInMonth = feasible.filter(r => r.month_total === 1).sort((a, b) => b.overall - a.overall);
                      if (neverInMonth.length === 0 && onceInMonth.length === 0) return null;
                      return (
                        <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg p-5 mt-4 text-white">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                              Block 4 — Rare in {monFull} (22 Years)
                            </h3>
                          </div>
                          <p className="text-sm text-white/80 mb-4">
                            Differences that exist overall but rarely/never appear in {monFull} — potential overdue signals
                          </p>
                          {neverInMonth.length > 0 && (
                            <div className="mb-3">
                              <div className="text-xs font-bold uppercase tracking-wider text-white/70 mb-2">Never appeared in {monFull}</div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {neverInMonth.slice(0, 8).map(row => (
                                  <div key={row.difference} className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 text-center">
                                    <div className="font-mono font-bold text-lg">
                                      {row.difference > 0 ? '+' : ''}{row.difference}
                                    </div>
                                    <div className="text-xs text-white/70">Number {row.next_value}</div>
                                    <div className="text-xs text-white/90 mt-1">
                                      {row.overall}x overall ({row.overall_pct}%)
                                    </div>
                                    <div className="text-xs font-bold text-amber-200 mt-0.5">
                                      0x in {monFull}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {onceInMonth.length > 0 && (
                            <div>
                              <div className="text-xs font-bold uppercase tracking-wider text-white/70 mb-2">Only 1x in {monFull} (22 years)</div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {onceInMonth.slice(0, 8).map(row => (
                                  <div key={row.difference} className="bg-white/15 backdrop-blur rounded-lg px-3 py-2 text-center">
                                    <div className="font-mono font-bold text-lg">
                                      {row.difference > 0 ? '+' : ''}{row.difference}
                                    </div>
                                    <div className="text-xs text-white/70">Number {row.next_value}</div>
                                    <div className="text-xs text-white/90 mt-1">
                                      {row.overall}x overall ({row.overall_pct}%)
                                    </div>
                                    <div className="text-xs font-bold text-amber-200 mt-0.5">
                                      1x in {monFull}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </TabsContent>
                );
              })}
            </Tabs>

              {/* Per-Ball Difference Overdue */}
              {data?.per_ball_diff_overdue && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-cyan-700 to-teal-700">
                    <h4 className="text-sm font-bold text-white">Block 5 — Per-Ball Difference Overdue</h4>
                    <p className="text-xs text-cyan-200 mt-0.5">
                      Top 5 most overdue specific differences for each ball — integrated into probability scoring (W9: 14% weight)
                    </p>
                  </div>
                  <div className="p-3">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      {['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, bi) => {
                        const info = data.per_ball_diff_overdue[bk];
                        if (!info) return null;
                        return (
                          <div key={bk} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                              <span className="font-bold text-xs text-gray-700">B{bi + 1}</span>
                              <span className="text-[10px] text-gray-400">Last: <span className="font-mono font-bold text-gray-600">{info.last_value}</span></span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {(info.top_overdue || []).map((o, oi) => {
                                const target = info.last_value + o.diff;
                                const validTarget = target >= 1 && target <= 50;
                                return (
                                  <div key={oi} className={`px-3 py-1.5 flex items-center justify-between ${o.is_overdue ? 'bg-red-50/50' : ''}`}>
                                    <div>
                                      <span className={`font-mono font-bold text-sm ${o.diff > 0 ? 'text-emerald-600' : o.diff < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                                        {o.label}
                                      </span>
                                      {validTarget && (
                                        <span className="text-[10px] text-gray-400 ml-1">
                                          {'\u2192'}{target}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <span className={`text-[10px] font-mono font-bold ${o.is_overdue ? 'text-red-600' : 'text-gray-400'}`}>
                                        {o.gap_ratio}x
                                      </span>
                                      <div className="text-[9px] text-gray-400">{o.count}x ({o.pct}%) gap {o.current_gap}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            </div>
            )}


            {/* Tab: Diff Range Coverage */}
            {mainTab === 'coverage' && coverageData?.balls && (() => {
              const balls = coverageData.balls;
              const ballLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };
              const highlightBrackets = [5,10,15,18,20,25];
              return (
                <div className="space-y-6">
                  {/* Key Milestones: Incremental bands that sum to 100% */}
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Block 1 — Coverage Milestones: 2026 Count vs Till 2025 %</h4>
                    <p className="text-xs text-gray-500 mb-3">Incremental bands: how many 2026 diffs fell in each range band. Bands sum to 100%.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-800 text-white">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold">Diff Band</th>
                            {Object.entries(ballLabels).map(([bk, label]) => (
                              <th key={bk} className="px-2 py-2.5 text-center font-bold" colSpan={4}>{label}</th>
                            ))}
                          </tr>
                          <tr className="text-[10px] bg-gray-700 text-gray-300">
                            <th></th>
                            {Object.keys(ballLabels).map(bk => (
                              <React.Fragment key={bk}>
                                <th className="px-1 py-1 text-center">2026</th>
                                <th className="px-1 py-1 text-center">2026%</th>
                                <th className="px-1 py-1 text-center">Till25%</th>
                                <th className="px-1 py-1 text-center">Diff</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const bands = [
                              { code: 'A', label: '±1 to ±5',   lo: 0,  hi: 5 },
                              { code: 'B', label: '±6 to ±10',  lo: 5,  hi: 10 },
                              { code: 'C', label: '±11 to ±15', lo: 10, hi: 15 },
                              { code: 'D', label: '±16 to ±20', lo: 15, hi: 20 },
                              { code: 'E', label: '±21 to ±25', lo: 20, hi: 25 },
                              { code: 'F', label: '> ±25',      lo: 25, hi: 999 },
                            ];
                            return bands.map((band, bi) => (
                              <tr key={band.label} className={bi === 0 ? 'bg-emerald-50/50 font-bold' : bi === bands.length - 1 ? 'bg-red-50/50' : 'border-b'}>
                                <td className="px-3 py-2 font-mono font-bold text-[#002FA7] whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-[#002FA7] text-white text-xs mr-2 font-bold">{band.code}</span>
                                  {band.label}
                                </td>
                                {Object.keys(ballLabels).map(bk => {
                                  const cov = balls[bk]?.coverage || [];
                                  const cumHi = cov.find(x => x.bracket === band.hi) || (band.hi === 999 ? { count_2026: balls[bk]?.total_diffs_2026 || 0, count_2025: balls[bk]?.total_diffs_2025 || 0, pct_2026: 100, pct_2025: 100 } : null);
                                  const cumLo = cov.find(x => x.bracket === band.lo);
                                  const n2026 = balls[bk]?.total_diffs_2026 || 1;
                                  const n2025 = balls[bk]?.total_diffs_2025 || 1;
                                  const cnt26 = (cumHi?.count_2026 || 0) - (cumLo?.count_2026 || 0);
                                  const cnt25 = (cumHi?.count_2025 || 0) - (cumLo?.count_2025 || 0);
                                  const pct26 = Math.round(cnt26 / n2026 * 1000) / 10;
                                  const pct25 = Math.round(cnt25 / n2025 * 1000) / 10;
                                  return (
                                    <React.Fragment key={bk}>
                                      <td className="px-1 py-2 text-center font-mono font-bold text-base">{cnt26}</td>
                                      <td className={`px-1 py-2 text-center font-mono text-sm font-bold ${pct26 >= 30 ? 'text-emerald-700' : pct26 >= 15 ? 'text-blue-600' : pct26 > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{pct26}%</td>
                                      <td className="px-1 py-2 text-center font-mono text-sm text-gray-500">{pct25}%</td>
                                      <td className={`px-1 py-2 text-center font-mono text-sm font-bold ${(pct26 - pct25) > 5 ? 'text-emerald-600' : (pct26 - pct25) < -5 ? 'text-red-600' : 'text-gray-400'}`}>{pct26 - pct25 > 0 ? '+' : ''}{Math.round((pct26 - pct25) * 10) / 10}%</td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            ));
                          })()}
                          <tr className="border-t-2 border-gray-800 bg-gray-100 font-bold">
                            <td className="px-3 py-2 font-bold">Total</td>
                            {Object.keys(ballLabels).map(bk => {
                              const n2026 = balls[bk]?.total_diffs_2026 || 0;
                              return (
                                <React.Fragment key={bk}>
                                  <td className="px-1 py-2 text-center font-mono font-bold">{n2026}</td>
                                  <td className="px-1 py-2 text-center font-mono font-bold">100%</td>
                                  <td className="px-1 py-2 text-center font-mono font-bold text-gray-500">100%</td>
                                  <td className="px-1 py-2 text-center font-mono font-bold text-gray-400">0%</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Ball Position by Band + Band Combination Patterns (side-by-side) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* LEFT: Ball Position by Band — Till 2025 ranking per band */}
                    <div className="bg-white border rounded-lg overflow-hidden" data-testid="ball-rank-by-band">
                      <div className="bg-indigo-700 text-white px-4 py-3">
                        <h4 className="font-bold">Block 2 — Ball Position by Band (Till 2025)</h4>
                        <p className="text-[10px] text-white/70">For each band, balls are ranked from highest to lowest Till-2025 % appearance</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-bold">Band</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">#1</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">#2</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">#3</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">#4</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">#5</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(coverageData.ball_rank_by_band || {}).map(([code, data]) => (
                              <tr key={code} className="border-b hover:bg-gray-50">
                                <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-[#002FA7] text-white text-xs mr-2 font-bold">{code}</span>
                                  <span className="text-gray-700 text-xs">{data.label}</span>
                                </td>
                                {data.rows.map((r, i) => (
                                  <td key={r.ball} className="px-2 py-2 text-center">
                                    <div className={`font-bold font-mono ${i === 0 ? 'text-emerald-700' : i === 1 ? 'text-blue-700' : i === 4 ? 'text-red-600' : 'text-gray-700'}`}>{r.ball}</div>
                                    <div className={`text-[10px] font-mono ${i === 0 ? 'text-emerald-600' : i === 4 ? 'text-red-500' : 'text-gray-500'}`}>{r.pct}%</div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-4 py-2 bg-gray-50 border-t text-[11px] text-gray-500">
                        Each row totals 100% across all 6 bands for that ball. Top = most frequent band for that ball.
                      </div>
                    </div>

                    {/* RIGHT: Top 10 Band Combination Patterns from Till 2025 */}
                    <div className="bg-white border rounded-lg overflow-hidden" data-testid="band-pattern-combos">
                      <div className="bg-purple-700 text-white px-4 py-3">
                        <h4 className="font-bold">Block 3 — Top 10 Band Combination Patterns (Till 2025)</h4>
                        <p className="text-[10px] text-white/70">
                          5-letter signature per draw = B1B2B3B4B5 band codes.
                          {coverageData.latest_pattern && (
                            <> Latest draw <span className="font-mono font-bold bg-white/20 px-1.5 py-0.5 rounded ml-1">#{coverageData.latest_pattern.draw_number}</span> ({coverageData.latest_pattern.draw_date}) = <span className="font-mono font-bold text-amber-300">{coverageData.latest_pattern.pattern}</span></>
                          )}
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Pattern</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(coverageData.top_patterns_till25 || []).map((p, i) => {
                              const isLatest = coverageData.latest_pattern && p.pattern === coverageData.latest_pattern.pattern;
                              return (
                                <tr key={p.pattern} className={`border-b ${isLatest ? 'bg-amber-100 font-bold' : i < 3 ? 'bg-purple-50' : ''}`}>
                                  <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                  <td className="px-3 py-2 text-center font-mono font-bold tracking-widest text-lg text-purple-900">
                                    {p.pattern}
                                    {isLatest && <span className="ml-2 text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-sans">LATEST</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center font-mono">{p.count_2025}</td>
                                  <td className="px-3 py-2 text-center font-mono text-blue-600">{p.pct_2025}%</td>
                                  <td className="px-3 py-2 text-center font-mono">{p.count_2026}</td>
                                  <td className="px-3 py-2 text-center font-mono text-amber-700">{p.pct_2026}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-4 py-2 bg-gray-50 border-t text-[11px] text-gray-500 flex items-center justify-between">
                        <span>Top 10 patterns shown</span>
                        <span>Total draws: Till25={coverageData.total_patterns_2025} / 2026={coverageData.total_patterns_2026}</span>
                      </div>
                    </div>
                  </div>

                  {/* All 2026 draws with their 5-letter band pattern */}
                  <div className="bg-white border rounded-lg overflow-hidden" data-testid="draws-2026-patterns">
                    <div className="bg-amber-600 text-white px-4 py-3">
                      <h4 className="font-bold">Block 4 — All 2026 Draws — Band Pattern per Draw ({coverageData.draws_2026_with_patterns?.length || 0})</h4>
                      <p className="text-[10px] text-white/80">Newest first. Pattern = B1B2B3B4B5 band letters (A=±1-5, B=±6-10, C=±11-15, D=±16-20, E=±21-25, F=&gt;±25)</p>
                    </div>
                    <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Draw</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Date</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">B1</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">B2</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">B3</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">B4</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">B5</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-gray-200">Diffs</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-amber-100">Pattern</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25 Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(coverageData.draws_2026_with_patterns || []).map((d, i) => {
                            const till25Row = (coverageData.top_patterns_till25 || []).find(p => p.pattern === d.pattern);
                            return (
                              <tr key={d.draw_number} className={`border-b hover:bg-amber-50 ${i === 0 ? 'bg-amber-50 font-bold' : ''}`}>
                                <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold">#{d.draw_number}</td>
                                <td className="px-3 py-2 text-center text-xs">{d.draw_date}</td>
                                {(d.ball_values || []).map((v, j) => (
                                  <td key={j} className="px-2 py-2 text-center font-mono text-[#002FA7] font-bold">{v}</td>
                                ))}
                                <td className="px-3 py-2 text-center font-mono text-[10px] text-gray-500 bg-gray-50">
                                  {(d.ball_diffs || []).map((df, j) => (
                                    <span key={j} className={df > 0 ? 'text-emerald-600' : df < 0 ? 'text-red-600' : 'text-gray-500'}>
                                      {j > 0 && <span className="text-gray-300">/</span>}{df > 0 ? `+${df}` : df}
                                    </span>
                                  ))}
                                </td>
                                <td className="px-3 py-2 text-center font-mono font-bold tracking-widest text-base text-purple-900">{d.pattern}</td>
                                <td className="px-3 py-2 text-center font-mono text-blue-600 text-xs">
                                  {till25Row ? `${till25Row.count_2025} (${till25Row.pct_2025}%)` : <span className="text-gray-400 italic">not in top 10</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Top 10 Diffs in 2026 + Lacking in 2026 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Top 10 Most Appearing in 2026 */}
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="bg-emerald-700 text-white px-4 py-3">
                        <h4 className="font-bold">Block 5 — Top 10 Differences — Most in 2026</h4>
                        <p className="text-[10px] text-white/70">All 5 balls combined: which diffs appeared most in 2026 vs Till 2025</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Diff</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Diff%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(coverageData.top_diffs_2026 || []).map((d, i) => (
                              <tr key={d.diff} className={`border-b ${i < 3 ? 'bg-emerald-50' : ''}`}>
                                <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                <td className={`px-3 py-2 text-center font-mono font-bold text-lg ${d.diff > 0 ? 'text-blue-700' : d.diff < 0 ? 'text-red-700' : 'text-gray-500'}`}>{d.diff > 0 ? '+' : ''}{d.diff}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold">{d.count_2026}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-amber-700">{d.pct_2026}%</td>
                                <td className="px-3 py-2 text-center font-mono">{d.count_2025}</td>
                                <td className="px-3 py-2 text-center font-mono text-blue-600">{d.pct_2025}%</td>
                                <td className={`px-3 py-2 text-center font-mono font-bold ${d.pct_diff > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{d.pct_diff > 0 ? '+' : ''}{d.pct_diff}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Top 10 Lacking in 2026 */}
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="bg-red-700 text-white px-4 py-3">
                        <h4 className="font-bold">Block 6 — Top 10 Differences — Lacking in 2026</h4>
                        <p className="text-[10px] text-white/70">High in Till 2025 but low/missing in 2026 — overdue diffs likely to reappear</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Diff</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Gap</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(coverageData.lacking_in_2026 || []).map((d, i) => (
                              <tr key={d.diff} className={`border-b ${i < 3 ? 'bg-red-50' : ''}`}>
                                <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                <td className={`px-3 py-2 text-center font-mono font-bold text-lg ${d.diff > 0 ? 'text-blue-700' : d.diff < 0 ? 'text-red-700' : 'text-gray-500'}`}>{d.diff > 0 ? '+' : ''}{d.diff}</td>
                                <td className="px-3 py-2 text-center font-mono">{d.count_2025}</td>
                                <td className="px-3 py-2 text-center font-mono text-blue-600">{d.pct_2025}%</td>
                                <td className="px-3 py-2 text-center font-mono font-bold">{d.count_2026}</td>
                                <td className="px-3 py-2 text-center font-mono text-amber-700">{d.pct_2026}%</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-red-600">+{d.gap}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Hot / Cold Differences — biggest % deltas vs Till 2025 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* HOT */}
                    <div className="bg-white border rounded-lg overflow-hidden" data-testid="hot-diffs-table">
                      <div className="bg-orange-600 text-white px-4 py-3 flex items-center gap-2">
                        <span className="text-xl">🔥</span>
                        <div>
                          <h4 className="font-bold">Block 7 — Top 10 HOT Differences — Biggest % Jump in 2026</h4>
                          <p className="text-[10px] text-white/70">Sorted by highest positive Δ (2026% − Till25%) — diffs over-indexing vs history</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Diff</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(coverageData.hot_diffs_2026 || []).map((d, i) => (
                              <tr key={d.diff} className={`border-b ${i < 3 ? 'bg-orange-50' : ''}`}>
                                <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                <td className={`px-3 py-2 text-center font-mono font-bold text-lg ${d.diff > 0 ? 'text-blue-700' : d.diff < 0 ? 'text-red-700' : 'text-gray-500'}`}>{d.diff > 0 ? '+' : ''}{d.diff}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold">{d.count_2026}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-amber-700">{d.pct_2026}%</td>
                                <td className="px-3 py-2 text-center font-mono">{d.count_2025}</td>
                                <td className="px-3 py-2 text-center font-mono text-blue-600">{d.pct_2025}%</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-orange-600">+{d.delta}%</td>
                              </tr>
                            ))}
                            {(!coverageData.hot_diffs_2026 || coverageData.hot_diffs_2026.length === 0) && (
                              <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400 text-xs">No diffs are over-indexing in 2026 yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* COLD */}
                    <div className="bg-white border rounded-lg overflow-hidden" data-testid="cold-diffs-table">
                      <div className="bg-sky-700 text-white px-4 py-3 flex items-center gap-2">
                        <span className="text-xl">❄️</span>
                        <div>
                          <h4 className="font-bold">Block 8 — Top 10 COLD Differences — Biggest % Drop in 2026</h4>
                          <p className="text-[10px] text-white/70">Sorted by most negative Δ (2026% − Till25%) — diffs under-indexing vs history</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Diff</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till25%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026</th>
                              <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026%</th>
                              <th className="px-3 py-2 text-center text-xs font-bold">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(coverageData.cold_diffs_2026 || []).map((d, i) => (
                              <tr key={d.diff} className={`border-b ${i < 3 ? 'bg-sky-50' : ''}`}>
                                <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                <td className={`px-3 py-2 text-center font-mono font-bold text-lg ${d.diff > 0 ? 'text-blue-700' : d.diff < 0 ? 'text-red-700' : 'text-gray-500'}`}>{d.diff > 0 ? '+' : ''}{d.diff}</td>
                                <td className="px-3 py-2 text-center font-mono">{d.count_2025}</td>
                                <td className="px-3 py-2 text-center font-mono text-blue-600">{d.pct_2025}%</td>
                                <td className="px-3 py-2 text-center font-mono font-bold">{d.count_2026}</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-amber-700">{d.pct_2026}%</td>
                                <td className="px-3 py-2 text-center font-mono font-bold text-sky-700">{d.delta}%</td>
                              </tr>
                            ))}
                            {(!coverageData.cold_diffs_2026 || coverageData.cold_diffs_2026.length === 0) && (
                              <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400 text-xs">No diffs are under-indexing in 2026 yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Per-draw Band Frequency Matrix (A/B/C/D/E/F counts) */}
                  <div className="bg-white border rounded-lg overflow-hidden" data-testid="draws-2026-band-matrix">
                    <div className="bg-emerald-700 text-white px-4 py-3">
                      <h4 className="font-bold">Block 9 — 2026 Draws — Band Frequency Matrix</h4>
                      <p className="text-[10px] text-white/80">Summary (Till 2025 vs 2026) on top. Per-draw rows below — each row sums to 5. Newest first.</p>
                    </div>
                    <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Draw</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Date</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">Pattern</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-emerald-50">A</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">B</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-purple-50">C</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-orange-50">D</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-red-50">E</th>
                            <th className="px-3 py-2 text-center text-xs font-bold bg-gray-200">F</th>
                          </tr>
                          <tr className="bg-gray-50 text-[10px] text-gray-500">
                            <th></th><th></th><th></th><th></th>
                            <th className="px-1 py-1">±1-5</th>
                            <th className="px-1 py-1">±6-10</th>
                            <th className="px-1 py-1">±11-15</th>
                            <th className="px-1 py-1">±16-20</th>
                            <th className="px-1 py-1">±21-25</th>
                            <th className="px-1 py-1">&gt;±25</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* ==== Summary rows: Till 2025, 2026, Diff ==== */}
                          {(() => {
                            const summary = coverageData.band_summary || [];
                            const total25 = coverageData.total_balls_till25 || 0;
                            const total26 = coverageData.total_balls_2026 || 0;
                            return (
                              <>
                                <tr className="border-b-2 border-gray-300 bg-blue-50/50 font-bold">
                                  <td className="px-3 py-2 text-right text-xs text-gray-500" colSpan={3}>Till 2025 — Count</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs text-blue-700">{total25.toLocaleString()}</td>
                                  {summary.map(s => (
                                    <td key={s.code} className="px-2 py-2 text-center font-mono text-base text-blue-800">{s.count_till25.toLocaleString()}</td>
                                  ))}
                                </tr>
                                <tr className="border-b-2 border-gray-300 bg-blue-50/30">
                                  <td className="px-3 py-2 text-right text-xs text-gray-500" colSpan={3}>Till 2025 — %</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs text-blue-600">100%</td>
                                  {summary.map(s => (
                                    <td key={s.code} className="px-2 py-2 text-center font-mono text-sm text-blue-600 font-bold">{s.pct_till25}%</td>
                                  ))}
                                </tr>
                                <tr className="border-b-2 border-gray-400 bg-amber-50/70 font-bold">
                                  <td className="px-3 py-2 text-right text-xs text-gray-500" colSpan={3}>2026 — Count</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs text-amber-800">{total26}</td>
                                  {summary.map(s => (
                                    <td key={s.code} className="px-2 py-2 text-center font-mono text-lg text-[#002FA7]">{s.count_2026}</td>
                                  ))}
                                </tr>
                                <tr className="border-b-2 border-gray-400 bg-amber-50/40">
                                  <td className="px-3 py-2 text-right text-xs text-gray-500" colSpan={3}>2026 — %</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs text-amber-700">100%</td>
                                  {summary.map(s => (
                                    <td key={s.code} className="px-2 py-2 text-center font-mono text-sm text-amber-700 font-bold">{s.pct_2026}%</td>
                                  ))}
                                </tr>
                                <tr className="border-b-4 border-gray-800 bg-gray-100 font-bold">
                                  <td className="px-3 py-2 text-right text-xs text-gray-500" colSpan={3}>Diff (2026% − Till25%)</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs text-gray-400">—</td>
                                  {summary.map(s => (
                                    <td key={s.code} className={`px-2 py-2 text-center font-mono text-sm font-bold ${s.pct_diff > 0 ? 'text-emerald-600' : s.pct_diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                      {s.pct_diff > 0 ? '+' : ''}{s.pct_diff}%
                                    </td>
                                  ))}
                                </tr>
                              </>
                            );
                          })()}
                          {/* ==== Per-draw rows ==== */}
                          {(() => {
                            const bandCodes = ['A','B','C','D','E','F'];
                            return (coverageData.draws_2026_with_patterns || []).map((d, i) => {
                              const counts = { A:0, B:0, C:0, D:0, E:0, F:0 };
                              (d.pattern || '').split('').forEach(ch => { if (counts[ch] !== undefined) counts[ch]++; });
                              const cellBg = (n, code) => {
                                if (!n) return 'text-gray-300';
                                const base = {
                                  A: 'text-emerald-700 bg-emerald-50',
                                  B: 'text-blue-700 bg-blue-50',
                                  C: 'text-purple-700 bg-purple-50',
                                  D: 'text-orange-700 bg-orange-50',
                                  E: 'text-red-700 bg-red-50',
                                  F: 'text-gray-800 bg-gray-200',
                                }[code];
                                return `font-bold ${base}`;
                              };
                              return (
                                <tr key={d.draw_number} className={`border-b hover:bg-emerald-50/40 ${i === 0 ? 'bg-amber-50 font-bold' : ''}`}>
                                  <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                                  <td className="px-3 py-2 text-center font-mono font-bold">#{d.draw_number}</td>
                                  <td className="px-3 py-2 text-center text-xs">{d.draw_date}</td>
                                  <td className="px-3 py-2 text-center font-mono font-bold tracking-widest text-purple-900">{d.pattern}</td>
                                  {bandCodes.map(c => (
                                    <td key={c} className={`px-2 py-2 text-center font-mono text-base ${cellBg(counts[c], c)}`}>
                                      {counts[c] || ''}
                                    </td>
                                  ))}
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 border-t text-[11px] text-gray-500">
                      Each per-draw row sums to 5. Top 5 summary rows show Till 2025 (count + %), 2026 (count + %), and the Diff (%).
                      Green Diff = 2026 over-indexes in that band. Red Diff = 2026 under-indexes.
                    </div>
                  </div>

                </div>
              );
            })()}

            {/* Tab: 2026 Sum of Difference */}
            {mainTab === 'monthly' && coverageData && (
              <div className="space-y-4">
                {/* Total-of-5-ball-diff Range Distribution */}
                <div className="bg-white border rounded-lg overflow-hidden" data-testid="total-diff-distribution">
                  <div className="bg-gradient-to-r from-cyan-700 to-blue-700 text-white px-4 py-3">
                    <h4 className="font-bold">Block 1 — Total-of-5-Ball-Diff Range Distribution</h4>
                    <p className="text-[10px] text-white/80">
                      Historical range of the Total column (sum of B1-B5 diffs per draw).
                      {coverageData.total_diff_stats && (
                        <>
                          {' '}Observed range: <span className="font-mono font-bold">{coverageData.total_diff_stats.min}</span> to <span className="font-mono font-bold">+{coverageData.total_diff_stats.max}</span>
                          {' '}| Average: <span className="font-mono font-bold">{coverageData.total_diff_stats.avg}</span>
                          {' '}| Till 2025 sample: <span className="font-mono">{coverageData.total_diff_stats.n_till25}</span>
                          {' '}| 2026: <span className="font-mono">{coverageData.total_diff_stats.n_2026}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-center text-xs font-bold">Range (Total)</th>
                          <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till 2025 Count</th>
                          <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Till 2025 %</th>
                          <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026 Count</th>
                          <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">2026 %</th>
                          <th className="px-3 py-2 text-center text-xs font-bold">Δ (2026 − Till25)</th>
                          <th className="px-3 py-2 text-center text-xs font-bold">Distribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(coverageData.total_diff_distribution || []).map((r) => {
                          const delta = Math.round((r.pct_2026 - r.pct_till25) * 10) / 10;
                          const barWidth = Math.min(r.pct_till25 * 4, 100);
                          const bar2026 = Math.min(r.pct_2026 * 4, 100);
                          const isMode = r.pct_till25 >= 20;
                          return (
                            <tr key={r.range} className={`border-b hover:bg-gray-50 ${isMode ? 'bg-blue-50/40 font-bold' : ''}`}>
                              <td className="px-3 py-2 text-center font-mono font-bold">{r.range}</td>
                              <td className="px-3 py-2 text-center font-mono">{r.count_till25}</td>
                              <td className="px-3 py-2 text-center font-mono text-blue-600">{r.pct_till25}%</td>
                              <td className="px-3 py-2 text-center font-mono">{r.count_2026}</td>
                              <td className="px-3 py-2 text-center font-mono text-amber-700">{r.pct_2026}%</td>
                              <td className={`px-3 py-2 text-center font-mono font-bold ${delta > 3 ? 'text-emerald-600' : delta < -3 ? 'text-red-600' : 'text-gray-400'}`}>
                                {delta > 0 ? '+' : ''}{delta}%
                              </td>
                              <td className="px-3 py-2">
                                <div className="w-full bg-gray-100 rounded-full h-3 relative" style={{ minWidth: '160px' }}>
                                  <div className="absolute h-full rounded-full bg-blue-300/70" style={{ width: `${barWidth}%` }} />
                                  <div className="absolute h-full rounded-full bg-amber-500" style={{ width: `${bar2026}%` }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-3 py-2 text-xs text-gray-500 italic text-center" colSpan={7}>
                            <span className="inline-flex items-center gap-1 mr-3"><span className="w-3 h-3 rounded bg-blue-300/70" /> Till 2025 ({coverageData.total_diff_stats?.n_till25 || 0} draws)</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" /> 2026 ({coverageData.total_diff_stats?.n_2026 || 0} draws)</span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Monthly matrix */}
                <div className="bg-white border rounded-lg overflow-hidden" data-testid="monthly-signed-diff-2026">
                <div className="bg-slate-800 text-white px-4 py-3">
                  <h4 className="font-bold">Block 2 — Sum of Difference</h4>
                  <p className="text-[10px] text-white/70">
                    Grand Total on top (sum of all 2026 ball diffs vs previous draw). Month blocks below — each month shows its subtotal row followed by individual draws. Newest month first.
                  </p>
                </div>
                <div className="overflow-x-auto max-h-[720px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-center text-xs font-bold">Month</th>
                        <th className="px-3 py-2 text-center text-xs font-bold">Year</th>
                        <th className="px-3 py-2 text-center text-xs font-bold">Draw</th>
                        <th className="px-3 py-2 text-center text-xs font-bold">Date</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Ball 1</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Ball 2</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Ball 3</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Ball 4</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-blue-50">Ball 5</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-amber-100">Total</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-yellow-50">LS 1</th>
                        <th className="px-3 py-2 text-center text-xs font-bold bg-yellow-50">LS 2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Grand Total row */}
                      {(() => {
                        const gt = coverageData.grand_totals_2026 || { ball_diffs: [0,0,0,0,0], total_sum: 0, ls_diffs: [0,0], draw_count: 0 };
                        const cellColor = (v) => v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-600' : 'text-gray-500';
                        return (
                          <tr className="border-b-4 border-slate-800 bg-slate-100 font-bold">
                            <td className="px-3 py-3 text-center text-xs text-gray-600" colSpan={2}>G Total</td>
                            <td className="px-3 py-3 text-center text-xs text-gray-400">{gt.draw_count} draws</td>
                            <td className="px-3 py-3 text-center text-xs text-gray-400">2026</td>
                            {gt.ball_diffs.map((v, i) => (
                              <td key={i} className={`px-2 py-3 text-center font-mono text-base font-bold ${cellColor(v)}`}>{v > 0 ? `+${v}` : v}</td>
                            ))}
                            <td className={`px-2 py-3 text-center font-mono text-base font-bold ${cellColor(gt.total_sum)}`}>{gt.total_sum > 0 ? `+${gt.total_sum}` : gt.total_sum}</td>
                            <td className={`px-2 py-3 text-center font-mono text-base font-bold ${cellColor(gt.ls_diffs[0])}`}>{gt.ls_diffs[0] > 0 ? `+${gt.ls_diffs[0]}` : gt.ls_diffs[0]}</td>
                            <td className={`px-2 py-3 text-center font-mono text-base font-bold ${cellColor(gt.ls_diffs[1])}`}>{gt.ls_diffs[1] > 0 ? `+${gt.ls_diffs[1]}` : gt.ls_diffs[1]}</td>
                          </tr>
                        );
                      })()}
                      {/* Monthly blocks */}
                      {(coverageData.monthly_breakdown_2026 || []).map((m, mi) => {
                        const cellColor = (v) => v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-600' : 'text-gray-500';
                        const subColor = (v) => v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-500' : 'text-gray-500';
                        return (
                          <React.Fragment key={`month-${m.month}`}>
                            <tr className={`border-b-2 border-gray-500 font-bold ${mi % 2 === 0 ? 'bg-indigo-50' : 'bg-purple-50'}`}>
                              <td className="px-3 py-2 text-center text-xs font-bold text-indigo-700" rowSpan={m.draws.length + 1}>
                                <div className="text-lg">{m.month_name}</div>
                                <div className="text-[10px] text-gray-500">{m.month}/2026</div>
                                <div className="text-[10px] text-gray-400 mt-1">{m.draw_count} draws</div>
                              </td>
                              <td className="px-3 py-2 text-center text-xs text-gray-500">2026</td>
                              <td className="px-3 py-2 text-center text-xs text-gray-500 italic" colSpan={2}>Month Total</td>
                              {m.totals.map((v, i) => (
                                <td key={i} className={`px-2 py-2 text-center font-mono text-sm font-bold ${cellColor(v)}`}>{v > 0 ? `+${v}` : v}</td>
                              ))}
                              <td className={`px-2 py-2 text-center font-mono text-sm font-bold ${cellColor(m.total_sum)}`}>{m.total_sum > 0 ? `+${m.total_sum}` : m.total_sum}</td>
                              <td className={`px-2 py-2 text-center font-mono text-sm font-bold ${cellColor(m.ls_totals[0])}`}>{m.ls_totals[0] > 0 ? `+${m.ls_totals[0]}` : m.ls_totals[0]}</td>
                              <td className={`px-2 py-2 text-center font-mono text-sm font-bold ${cellColor(m.ls_totals[1])}`}>{m.ls_totals[1] > 0 ? `+${m.ls_totals[1]}` : m.ls_totals[1]}</td>
                            </tr>
                            {m.draws.map((d) => {
                              const rowSum = (d.ball_diffs || []).reduce((s, v) => s + v, 0);
                              return (
                                <tr key={d.draw_number} className="border-b hover:bg-indigo-50/40">
                                  <td className="px-3 py-2 text-center text-xs text-gray-500">2026</td>
                                  <td className="px-3 py-2 text-center font-mono text-xs font-bold">#{d.draw_number}</td>
                                  <td className="px-3 py-2 text-center text-[11px] text-gray-600">{d.draw_date}</td>
                                  {(d.ball_diffs || []).map((v, i) => (
                                    <td key={i} className={`px-2 py-2 text-center font-mono ${subColor(v)}`}>{v > 0 ? `+${v}` : v}</td>
                                  ))}
                                  <td className={`px-2 py-2 text-center font-mono font-bold ${cellColor(rowSum)}`}>{rowSum > 0 ? `+${rowSum}` : rowSum}</td>
                                  <td className={`px-2 py-2 text-center font-mono ${subColor(d.ls_diffs?.[0] ?? 0)}`}>{(d.ls_diffs?.[0] ?? 0) > 0 ? `+${d.ls_diffs[0]}` : (d.ls_diffs?.[0] ?? 0)}</td>
                                  <td className={`px-2 py-2 text-center font-mono ${subColor(d.ls_diffs?.[1] ?? 0)}`}>{(d.ls_diffs?.[1] ?? 0) > 0 ? `+${d.ls_diffs[1]}` : (d.ls_diffs?.[1] ?? 0)}</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 bg-gray-50 border-t text-[11px] text-gray-500 flex items-center justify-between">
                  <span>Signed per-ball / per-LS differences between each draw and the previous draw.</span>
                  <span className="text-gray-400">Green = positive jump, Red = negative jump.</span>
                </div>
              </div>

              {/* Per-Ball Number Frequency Diff (2026 vs Pre-2026) — moved to W7 Number Appearance */}
              </div>
            )}

          </>
        ) : null}
      </main>
    </div>
  );
}

// ============================================================
// W3 Delta Pattern Match Tab — per-ball increment predictor
// Same framework as W59: Actual + Adaptive + Fallback + Tier consensus
// Covers: Ball 1-5 + LS 1-2 (7 series total)
// ============================================================

function DeltaPatternTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patternLen, setPatternLen] = useState(5);
  const [tolerance, setTolerance] = useState(2);
  const [pctTolerance, setPctTolerance] = useState(15);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = (silent = false) => {
      if (!silent) setLoading(true);
      axios.get(`${API}/api/analysis/w3-delta-pattern-match?pattern_len=${patternLen}&tolerance=${tolerance}&pct_tolerance=${pctTolerance}`)
        .then(res => setData(res.data))
        .catch(() => setData(null))
        .finally(() => { if (!silent) setLoading(false); });
    };
    load();
    const onEvt = () => load(true);
    const onStorage = (e) => { if (e.key === 'euromillions:last_draw_update') load(true); };
    window.addEventListener('euromillions:draws-updated', onEvt);
    window.addEventListener('storage', onStorage);
    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') load(true); };
      }
    } catch {}
    return () => {
      window.removeEventListener('euromillions:draws-updated', onEvt);
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [patternLen, tolerance, pctTolerance, API]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /><span className="ml-3 text-gray-500">Scanning per-ball delta history...</span></div>;
  }
  if (!data || data.error) {
    return <div className="text-center text-gray-500 py-16">No delta pattern data available.</div>;
  }

  const tierColor = (t) => ({
    ELITE: 'bg-purple-600 text-white ring-purple-300',
    STRONG: 'bg-emerald-600 text-white ring-emerald-300',
    NORMAL: 'bg-blue-500 text-white ring-blue-300',
    WIDE: 'bg-orange-500 text-white ring-orange-300',
    UNRELIABLE: 'bg-gray-400 text-white ring-gray-300',
  }[t] || 'bg-gray-400 text-white ring-gray-300');

  const ballKeys = data.ball_keys || [];
  const perBall = data.per_ball || {};

  return (
    <div className="space-y-4" data-testid="delta-pattern-view">
      {/* Controls */}
      <div className="flex items-center gap-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Pattern length:</label>
          <select value={patternLen} onChange={e => setPatternLen(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-pattern-len">
            {[3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n} draws</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Actual tol:</label>
          <select value={tolerance} onChange={e => setTolerance(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-tolerance">
            {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>±{n}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Adaptive ±%:</label>
          <select value={pctTolerance} onChange={e => setPctTolerance(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-pct-tolerance">
            {[10, 15, 20, 25, 30, 40, 50].map(n => <option key={n} value={n}>±{n}%</option>)}
          </select>
        </div>
        <div className="text-[10px] text-indigo-700 italic ml-auto max-w-md">
          Pattern = last N deltas between consecutive draws per ball. Predicts next ball value = current + predicted delta.
        </div>
      </div>

      {/* Summary grid — all 7 balls' final bet cards */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 1</span>Predicted Next Ball Values — Quick Glance</h3>
          <p className="text-[11px] text-white/70 mt-0.5">Tier-adjusted consensus prediction per ball. Click any card for full details below.</p>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {ballKeys.map(({ key, label }) => {
            const b = perBall[key];
            if (!b || b.error) return (
              <div key={key} className="bg-gray-100 border border-gray-200 rounded p-2 text-center">
                <div className="text-[11px] font-bold text-gray-600">{label}</div>
                <div className="text-[10px] text-gray-400">No data</div>
              </div>
            );
            const c = b.consensus || {};
            const p = b.predicted_next_ball || {};
            return (
              <div key={key} className={`rounded p-2 border-2 text-center ${
                c.side_label === 'UP' ? 'bg-emerald-50 border-emerald-300' :
                c.side_label === 'DOWN' ? 'bg-rose-50 border-rose-300' :
                c.side_label === 'MIXED' ? 'bg-amber-50 border-amber-300' :
                'bg-gray-50 border-gray-300'
              }`} data-testid={`delta-summary-${key}`}>
                <div className="text-[11px] font-bold text-gray-700">{label}</div>
                <div className="text-[10px] text-gray-500">Now: <b className="text-gray-800 font-mono">{b.current_ball_value}</b></div>
                {p.center !== null && p.center !== undefined ? (
                  <>
                    <div className={`mt-1 text-lg font-black font-mono ${c.side_label === 'UP' ? 'text-emerald-800' : c.side_label === 'DOWN' ? 'text-rose-800' : 'text-amber-800'}`}>{p.center}</div>
                    <div className="text-[9px] text-gray-500 font-mono">{p.low}–{p.high}</div>
                    <div className={`mt-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full ring-2 ${tierColor(c.tier)}`}>
                      {c.tier === 'ELITE' ? '💎' : c.tier === 'STRONG' ? '🟢' : c.tier === 'NORMAL' ? '🔵' : c.tier === 'WIDE' ? '🟠' : '⚫'} {c.tier}
                    </div>
                    <div className="text-[9px] text-gray-400 mt-0.5">{c.effective_confidence_pct}% · n={c.used_matches_total}</div>
                  </>
                ) : (
                  <div className="mt-1 text-[10px] text-gray-400 italic py-3">Unprecedented</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed per-ball consensus + matrix */}
      <div className="space-y-3">
        {ballKeys.map(({ key, label }) => {
          const b = perBall[key];
          if (!b || b.error) return null;
          const c = b.consensus || {};
          const pn = b.predicted_next_ball || {};
          return (
            <div key={key} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`delta-detail-${key}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-gray-800">{label}</span>
                  <span className="text-[11px] text-gray-500">Current: <b className="font-mono text-gray-800">{b.current_ball_value}</b></span>
                  <span className="text-[11px] text-gray-500">Last 5 deltas:</span>
                  <div className="flex items-center gap-1">
                    {(b.pattern || []).map((v, i) => (
                      <span key={i} className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded ${i === b.pattern.length - 1 ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-gray-100 border border-gray-300 text-gray-700'}`}>
                        {v > 0 ? `+${v}` : v}
                      </span>
                    ))}
                  </div>
                </div>
                {c.bet_center !== null && c.bet_center !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ring-2 ${tierColor(c.tier)}`}>
                      {c.tier_label}
                    </span>
                  </div>
                )}
              </div>

              {/* Consensus bet */}
              {c.bet_center !== null && c.bet_center !== undefined ? (
                <div className={`rounded-lg p-3 border-2 mb-2 ${
                  c.side_label === 'UP' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400' :
                  c.side_label === 'DOWN' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-400' :
                  'bg-gray-50 border-gray-300'
                }`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Next Ball Value Prediction</div>
                      <div className={`text-2xl font-black font-mono ${c.side_label === 'UP' ? 'text-emerald-800' : c.side_label === 'DOWN' ? 'text-rose-800' : 'text-gray-800'}`} data-testid={`predicted-ball-${key}`}>
                        ~{pn.center} <span className="text-sm font-normal text-gray-500">(range {pn.low}–{pn.high})</span>
                      </div>
                      <div className="text-[11px] text-gray-600 mt-0.5">
                        = <b>{b.current_ball_value}</b> + delta ~<b>{pn.predicted_delta}</b> · {c.side_label} · {c.formula_used}
                      </div>
                    </div>
                    <div className="text-right text-[10px] text-gray-600">
                      <div>Direction conf: <b className={
                        c.tier === 'ELITE' ? 'text-purple-700' : c.tier === 'STRONG' ? 'text-emerald-700' :
                        c.tier === 'NORMAL' ? 'text-blue-700' : c.tier === 'WIDE' ? 'text-orange-700' : 'text-gray-500'
                      }>{c.effective_confidence_pct}%</b> <span className="text-gray-300 text-[9px]">(raw {c.direction_confidence_pct}%)</span></div>
                      <div>Mode consensus: <b className="font-mono">{c.mode_consensus !== null && c.mode_consensus !== undefined ? (c.mode_consensus > 0 ? `+${c.mode_consensus}` : c.mode_consensus) : '-'}</b></div>
                      <div>Sample: <b>{c.used_matches_total}m</b> · Band <b>±{c.band_pct}%</b></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-300 rounded p-3 text-sm text-gray-500 italic mb-2">
                  ⚫ No historical precedent for this delta pattern (all scales have {'<'} 3 matches).
                </div>
              )}

              {/* Primary mode counts + fallback matrix if triggered */}
              <div className="flex gap-2 flex-wrap text-[10px] text-gray-500">
                <span>Actual ±{b.actual.tolerance}: <b className="text-gray-700">{b.actual.total_matches}m</b></span>
                <span>Adaptive ±{b.adaptive.pct_tolerance}%: <b className="text-gray-700">{b.adaptive.total_matches}m</b></span>
                {b.fallback_triggered && b.multi_scale_verdict && (
                  <span className={`px-2 rounded ${
                    b.multi_scale_verdict.level === 'SUPER_STRONG' ? 'bg-emerald-100 text-emerald-800' :
                    b.multi_scale_verdict.level === 'STRONG' ? 'bg-emerald-100 text-emerald-700' :
                    b.multi_scale_verdict.level === 'MODERATE' ? 'bg-amber-100 text-amber-800' :
                    b.multi_scale_verdict.level === 'CONFLICTING' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>⚡ {b.multi_scale_verdict.label}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// =====================================================================================
// W3 Ball-Diff — Convergence Tab (per-ball ELITE/STRONG/MODERATE + backtest)
// =====================================================================================
function W3BallDiffConvergenceTab({ data }) {
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const lastVals = data.last_values || {};
  // Predicted number = last_value + diff. Clamp to valid main-ball range (1..50).
  const predictNum = (bk, d) => {
    const lv = lastVals[bk];
    if (lv == null || d == null) return null;
    const n = lv + d;
    if (n < 1 || n > 50) return null;
    return n;
  };
  const renderDiff = (d, bk) => {
    const num = predictNum(bk, d);
    return (
      <span className="inline-flex items-center gap-1">
        <span className={`font-mono font-bold ${d > 0 ? 'text-emerald-700' : d < 0 ? 'text-red-600' : 'text-gray-600'}`}>
          {d > 0 ? '+' : ''}{d}
        </span>
        {num != null && (
          <span className="font-mono text-[11px] text-gray-500">({num})</span>
        )}
      </span>
    );
  };
  return (
    <div className="space-y-5" data-testid="w3-convergence-tab">
      <BacktestPanel endpoint="ball-diff" />

      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 rounded-xl p-5 text-white shadow-xl">
        <h2 className="text-xl font-black mb-1">Per-Ball Convergence — Ball Differences</h2>
        <p className="text-[11px] text-emerald-100">Complementary layer: Shows where Formula 1 (long-term overdue) and Formula 2 (next-draw contextual) agree on the same diff for each ball.</p>
      </div>

      <div className="space-y-4">
        {ballKeys.map((bk, bi) => {
          const ball = data.balls?.[bk];
          const conv = ball?.convergence;
          if (!conv) return null;
          const ballPicks = [
            ...(conv.elite || []).map(id => ({ id, tier: 'ELITE' })),
            ...(conv.strong || []).map(id => ({ id, tier: 'STRONG' })),
          ];
          return (
            <div key={bk} className="bg-white border-2 border-emerald-200 rounded-xl overflow-hidden shadow-sm" data-testid={`ball-conv-${bi + 1}`}>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b-2 border-emerald-200 px-5 py-3 flex items-center gap-3">
                <span className="bg-emerald-600 text-white font-black text-base px-3 py-1 rounded">Ball {bi + 1}</span>
                <span className="text-[11px] text-gray-500 font-mono">Last: <span className="font-bold text-gray-700">{lastVals[bk]}</span> · Last diff: {renderDiff(conv.last_diff, bk)}</span>
              </div>
              {ball?.risk_flags && ballPicks.some(p => ball.risk_flags[String(p.id)]) && (
                <div className="px-4 pt-3">
                  <BlindSpotsBanner
                    riskFlags={ball.risk_flags}
                    topPicks={ballPicks}
                    itemLabel="diffs"
                    renderItem={(d) => renderDiff(parseInt(d, 10), bk)}
                  />
                </div>
              )}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3" data-testid={`ball-${bi + 1}-elite`}>
                    <div className="text-[11px] font-black text-amber-900 mb-2">🥇 ELITE (top-3 in BOTH)</div>
                    {conv.elite?.length === 0 ? (
                      <p className="text-[10px] text-amber-700 italic">—</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {conv.elite.map((d, i) => (
                          <span key={i} className="inline-flex items-center bg-amber-300 text-amber-950 rounded px-3 py-1.5 font-mono font-black text-base shadow ring-1 ring-amber-400">
                            {renderDiff(d, bk)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-3" data-testid={`ball-${bi + 1}-strong`}>
                    <div className="text-[11px] font-black text-blue-900 mb-2">🥈 STRONG</div>
                    {conv.strong?.length === 0 ? (
                      <p className="text-[10px] text-blue-700 italic">—</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {conv.strong.map((d, i) => (
                          <span key={i} className="inline-flex items-center bg-blue-200 text-blue-900 rounded px-2.5 py-1 font-mono font-bold text-sm">{renderDiff(d, bk)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-3" data-testid={`ball-${bi + 1}-moderate`}>
                    <div className="text-[11px] font-black text-gray-700 mb-2">🥉 MODERATE</div>
                    {conv.moderate?.length === 0 ? (
                      <p className="text-[10px] text-gray-500 italic">—</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {conv.moderate.map((d, i) => (
                          <span key={i} className="inline-flex items-center bg-gray-200 text-gray-700 rounded px-2 py-0.5 font-mono text-xs">{renderDiff(d, bk)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
                  <div className="bg-blue-50/40 rounded p-2 border border-blue-200">
                    <div className="font-bold text-blue-800 mb-1">F1 Top-5 (Long-term)</div>
                    <div className="flex gap-1.5">
                      {conv.f1_top5.map((d, i) => (
                        <span key={i} className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono ${i < 3 ? 'bg-blue-600 text-white font-bold' : 'bg-gray-200 text-gray-700'}`}>{renderDiff(d, bk)}</span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-orange-50/40 rounded p-2 border border-orange-200">
                    <div className="font-bold text-orange-800 mb-1">F2 Top-5 (Next-draw)</div>
                    <div className="flex gap-1.5">
                      {conv.f2_top5.map((d, i) => (
                        <span key={i} className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono ${i < 3 ? 'bg-orange-600 text-white font-bold' : 'bg-gray-200 text-gray-700'}`}>{renderDiff(d, bk)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================================================
// W3 Ball-Diff — Formula Tab (F1 or F2)
// =====================================================================================
function W3BallDiffFormulaTab({ data, which }) {
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const isF1 = which === 'f1';
  const accentColor = isF1 ? 'blue' : 'orange';
  const title = isF1 ? 'Formula 1 (Long-term)' : 'Formula 2 (Next-draw)';
  const subTitle = isF1
    ? 'Score = Freq% × (overdue_ratio)^0.5 × volatility × recency × consec-miss'
    : 'Score = T (trans×0.3) + G (gap×10 cap30) + Y (year×2 cap20) + B (freq×1.5) + DoW + M2 (pair×0.2)';
  const renderDiff = (d) => (
    <span className={`font-mono font-bold ${d > 0 ? 'text-emerald-700' : d < 0 ? 'text-red-600' : 'text-gray-600'}`}>
      {d > 0 ? '+' : ''}{d}
    </span>
  );
  return (
    <div className="space-y-5" data-testid={`w3-${which}-tab`}>
      <div className={`bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-700 rounded-xl p-5 text-white shadow-lg`}>
        <h2 className="text-xl font-black mb-1">{title} — Ball Differences</h2>
        <p className={`text-[11px] text-${accentColor}-100 font-mono`}>{subTitle}</p>
      </div>

      <div className="space-y-4">
        {ballKeys.map((bk, bi) => {
          const ball = data.balls?.[bk];
          const rows = (ball?.[isF1 ? 'f1_rows' : 'f2_rows'] || []);
          if (rows.length === 0) return null;
          const sorted = [...rows].sort((a, b) =>
            isF1
              ? (b.pred_score_volatility_adj || 0) - (a.pred_score_volatility_adj || 0)
              : (b.combined_score_v2 || 0) - (a.combined_score_v2 || 0),
          );
          const top3Threshold = sorted.length >= 3
            ? (isF1 ? sorted[2].pred_score_volatility_adj : sorted[2].combined_score_v2)
            : 0;
          return (
            <div key={bk} className={`bg-white border-2 border-${accentColor}-200 rounded-xl shadow-sm overflow-hidden`} data-testid={`w3-${which}-ball-${bi + 1}`}>
              <div className={`bg-${accentColor}-50 border-b-2 border-${accentColor}-200 px-5 py-3`}>
                <span className={`bg-${accentColor}-600 text-white font-black text-base px-3 py-1 rounded`}>Ball {bi + 1}</span>
                <span className="ml-2 text-[11px] text-gray-500">{sorted.length} unique diffs ranked</span>
              </div>
              <div className="overflow-x-auto max-h-[400px]">
                <table className="w-full text-xs">
                  <thead className={`bg-${accentColor}-50 sticky top-0`}>
                    <tr className={`text-[10px] uppercase tracking-wider text-${accentColor}-900 border-b`}>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-center">Diff</th>
                      <th className="px-2 py-2 text-center">Count</th>
                      <th className="px-2 py-2 text-center">Freq%</th>
                      {isF1 ? (
                        <>
                          <th className="px-2 py-2 text-center">Exp Gap</th>
                          <th className="px-2 py-2 text-center">Cur Gap</th>
                          <th className="px-2 py-2 text-center">Overdue</th>
                          <th className="px-2 py-2 text-center">CV</th>
                          <th className="px-2 py-2 text-center">Rec</th>
                          <th className="px-2 py-2 text-center">Consec</th>
                        </>
                      ) : (
                        <>
                          <th className="px-2 py-2 text-center">T</th>
                          <th className="px-2 py-2 text-center">G</th>
                          <th className="px-2 py-2 text-center">Y</th>
                          <th className="px-2 py-2 text-center">B</th>
                          <th className="px-2 py-2 text-center">DoW</th>
                          <th className="px-2 py-2 text-center">M2</th>
                        </>
                      )}
                      <th className={`px-2 py-2 text-center bg-${accentColor}-100 font-black`}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.slice(0, 30).map((r, i) => {
                      const score = isF1 ? r.pred_score_volatility_adj : r.combined_score_v2;
                      const isTop3 = score >= top3Threshold && top3Threshold > 0;
                      return (
                        <tr key={r.difference} className={`border-b border-gray-100 ${isTop3 ? `bg-${accentColor}-50` : 'hover:bg-gray-50'}`}>
                          <td className="px-2 py-2 font-mono text-gray-500">#{i + 1}</td>
                          <td className="px-2 py-2 text-center">{renderDiff(r.difference)}</td>
                          <td className="px-2 py-2 text-center font-mono font-bold">{r.count}</td>
                          <td className="px-2 py-2 text-center font-mono">{r.freq_pct}%</td>
                          {isF1 ? (
                            <>
                              <td className="px-2 py-2 text-center font-mono text-gray-500">{r.expected_gap}</td>
                              <td className="px-2 py-2 text-center font-mono">{r.current_gap}</td>
                              <td className={`px-2 py-2 text-center font-mono font-bold ${r.overdue_ratio >= 2 ? 'text-red-600' : r.overdue_ratio >= 1 ? 'text-amber-600' : 'text-gray-500'}`}>{r.overdue_ratio}x</td>
                              <td className="px-2 py-2 text-center font-mono text-[10px] text-gray-500">{r.gap_cv}</td>
                              <td className={`px-2 py-2 text-center font-mono text-[10px] ${r.recency_factor > 1 ? 'text-emerald-700' : r.recency_factor < 1 ? 'text-red-600' : 'text-gray-500'}`}>{r.recency_factor}</td>
                              <td className={`px-2 py-2 text-center font-mono text-[10px] ${r.consec_miss_factor > 1 ? 'text-emerald-700' : 'text-gray-500'}`}>{r.consec_miss_factor}</td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-2 text-center font-mono">
                                <div className="font-bold text-orange-700">{r.trans_score}</div>
                                <div className="text-[9px] text-gray-400">({r.trans_pct}%)</div>
                              </td>
                              <td className="px-2 py-2 text-center font-mono">
                                <div className="font-bold text-orange-700">{r.gap_score_v2}</div>
                                <div className="text-[9px] text-gray-400">({r.overdue_ratio}x)</div>
                              </td>
                              <td className="px-2 py-2 text-center font-mono">
                                <div className={`font-bold ${(r.year_score || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.year_score}</div>
                                <div className="text-[9px] text-gray-400">({r.year_diff > 0 ? '+' : ''}{r.year_diff}%)</div>
                              </td>
                              <td className="px-2 py-2 text-center font-mono font-bold text-orange-700">{r.base_score}</td>
                              <td className={`px-2 py-2 text-center font-mono ${(r.dow_bonus || 0) > 0 ? 'text-emerald-700 font-bold' : 'text-gray-400'}`}>{r.dow_bonus}</td>
                              <td className={`px-2 py-2 text-center font-mono ${(r.markov2_score || 0) > 0 ? 'text-emerald-700 font-bold' : 'text-gray-400'}`}>{r.markov2_score}</td>
                            </>
                          )}
                          <td className="px-2 py-2 text-center">
                            {isTop3 ? (
                              <span className={`inline-flex w-12 h-12 rounded-full items-center justify-center bg-${accentColor}-500 text-white font-mono font-black text-sm shadow ring-2 ring-${accentColor}-200`}>
                                {score}
                              </span>
                            ) : (
                              <span className={`font-mono font-bold text-${accentColor}-800`}>{score}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {sorted.length > 30 && (
                  <div className="p-2 text-center text-[10px] text-gray-500 bg-gray-50 border-t">Showing top 30 of {sorted.length} diffs</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
